/**
 * Palm trees — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_motifs_ii`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const palmTreesPack: ToilePack = {
    slug: 'palm-trees',
    category: 'motif',
    name: 'Palm trees',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 832, h: 1216},
    readings: [
        {key: 'single', label: 'Single palm', subject: 'A single tall coconut palm with a curving trunk and a crown of fronds, a few coconuts, drawn whole on a plain ground.'},
        {key: 'row', label: 'Boulevard row', subject: 'A row of tall thin palms receding along a boulevard against a pink sunset sky, their crowns small and high, long shadows on the road.'},
        {key: 'frond', label: 'Frond close up', subject: 'A single palm frond filling the frame, every leaflet drawn, light through the leaves, a plain ground.'},
    ],
    styles: [
        {key: 'line', label: 'Line drawing', style: 'single-weight black line drawing, clean contour, white ground', palette: 'black ink only, no other colour', framing: 'centred on a plain white background with a wide even margin'},
        {key: 'poster', label: 'Flat poster', style: 'flat travel-poster illustration, limited palette, clean shapes, print texture', palette: 'a limited travel-poster palette of green, pink and cream', framing: 'centred on a plain background with a thin printed border'},
        {key: 'watercolour', label: 'Watercolour', style: 'soft watercolour illustration, gentle washes, white paper', palette: 'green and teal washes with a warm sunset glow', framing: 'set on a plain white paper background, the wash fading toward a soft margin'},
    ],
    directions: ['a coconut tree pair', 'at sunset only', 'for a beach towel'],
    directionHint: 'try: a coconut tree pair',
    seo: {
        title: 'Palm tree art: single, boulevard row, frond',
        metaDescription: 'Nine palm tree illustrations — a single palm, a boulevard row and a close-up frond — in line, flat travel-poster and watercolour styles.',
        h1: 'Palm tree drawings: single, boulevard row, frond in three styles',
        tagline: 'Nine renders, three palm readings, three looks each — line to watercolour.',
        intro: 'The tree alone, without the beach scene around it: a single curving coconut palm, a receding row along a sunset boulevard, and a close-up frond with light through every leaflet. Each renders as clean line, a flat travel-poster print, or soft watercolour washes. Ask for a coconut-tree pair or a beach-towel crop and the same three looks carry it.',
        useCases: [
            'a beach-house or travel print',
            'a summer card or poster',
            'a beach towel or tote design',
            'a social media summer post',
        ],
        faq: [
            {q: 'What size do these print at?', a: 'Each is composed in a tall portrait crop, suited to a poster, a print or a card.'},
            {q: 'Can I get a pair of trees or a beach-towel crop?', a: 'Yes — the direction field takes any variation, and the same three styles render it.'},
            {q: "What's the difference between the three styles?", a: 'Line is a single black contour on white, flat poster holds a limited travel palette, and watercolour is a soft green-and-teal wash.'},
            {q: 'Is this a real photograph of a palm tree?', a: 'No — every image is an illustration, drawn in the chosen style, not a photo.'},
        ],
    },
}
